import { Editor } from "@tiptap/react";
import MenuBarItem from "./menu-bar-item";
import { Icon } from "@elaniintc/goes-design-system";

type EditorOption = {
  icon: React.ReactNode;
  onClick: () => boolean;
  pressed: boolean;
};

interface ManuBarProps {
  editor: Editor | null;
  isDisabled?: boolean;
  showHeadings?: boolean;
  showAlignment?: boolean;
  showIndent?: boolean;
  onInsertVariable?: () => void;
  onHyperlink?: () => void;
}

export default function BasicMenuBar({
  editor,
  isDisabled = false,
  showHeadings = false,
  showAlignment = false,
  showIndent = false,
  onInsertVariable,
  onHyperlink,
}: ManuBarProps) {
  if (!editor) return null;

  const headingOptions: EditorOption[] = [
    {
      icon: <span className="text-sm font-semibold text-body">H1</span>,
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      pressed: editor.isActive("heading", { level: 1 }),
    },
    {
      icon: <span className="text-sm font-semibold text-body">H2</span>,
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      pressed: editor.isActive("heading", { level: 2 }),
    },
    {
      icon: <span className="text-sm font-semibold text-body">H3</span>,
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      pressed: editor.isActive("heading", { level: 3 }),
    },
  ];

  const textOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="bold"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBold().run(),
      pressed: editor.isActive("bold"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="italic"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleItalic().run(),
      pressed: editor.isActive("italic"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="strikethrough"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleStrike().run(),
      pressed: editor.isActive("strike"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="underline"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleUnderline().run(),
      pressed: editor.isActive("underline"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="link"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => {
        if (editor.isActive("link")) {
          if (onHyperlink) {
            onHyperlink();
            return true;
          }
          return editor.chain().focus().unsetLink().run();
        }
        if (onHyperlink) {
          onHyperlink();
          return true;
        } else {
          const url = window.prompt("URL:");
          if (!url) return false;
          return editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
        }
      },
      pressed: editor.isActive("link"),
    },
  ];

  const listOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="list"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      pressed: editor.isActive("bulletList"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="list-ol"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      pressed: editor.isActive("orderedList"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="block-quote"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
      pressed: editor.isActive("blockquote"),
    },
  ];

  const alignmentOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="align-left"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("left").run(),
      pressed: editor.isActive({ textAlign: "left" }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="align-center"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("center").run(),
      pressed: editor.isActive({ textAlign: "center" }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="align-right"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("right").run(),
      pressed: editor.isActive({ textAlign: "right" }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="align-justify"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("justify").run(),
      pressed: editor.isActive({ textAlign: "justify" }),
    },
  ];

  const indentOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="outdent"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => {
        // Decrease indent
        const currentIndent =
          editor.getAttributes("indentedParagraph").indent || 0;
        if (currentIndent > 0) {
          return editor
            .chain()
            .focus()
            .updateAttributes("indentedParagraph", {
              indent: currentIndent - 1,
            })
            .run();
        }
        return false;
      },
      pressed: false,
    },
    {
      icon: (
        <Icon
          type="far"
          name="indent"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => {
        // Increase indent
        const currentIndent =
          editor.getAttributes("indentedParagraph").indent || 0;
        return editor
          .chain()
          .focus()
          .updateAttributes("indentedParagraph", { indent: currentIndent + 1 })
          .run();
      },
      pressed: false,
    },
  ];

  const renderGroup = (options: EditorOption[], testId?: string) => (
    <div
      className="flex shadow-custom bg-neutral-object rounded-md"
      data-testid={testId}
    >
      {options.map((option, index) => (
        <MenuBarItem
          key={index}
          type={
            index === 0
              ? "first"
              : index === options.length - 1
                ? "last"
                : undefined
          }
          isPressed={option.pressed}
          onClick={option.onClick}
          icon={option.icon}
          isDisabled={isDisabled}
        />
      ))}
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showHeadings && renderGroup(headingOptions, "heading-options-id")}
      {renderGroup(textOptions, "text-options-id")}
      {showAlignment && renderGroup(alignmentOptions, "alignment-options-id")}
      {renderGroup(listOptions, "text-list-id")}
      {showIndent && renderGroup(indentOptions, "indent-options-id")}
      {onInsertVariable && (
        <div
          className="flex shadow-custom bg-neutral-object rounded-md"
          data-testid="variable-button-id"
        >
          <MenuBarItem
            type="single"
            isPressed={false}
            onClick={() => {
              onInsertVariable();
              return true;
            }}
            icon={
              <span className="text-sm font-semibold text-body">
                Variable +
              </span>
            }
            isDisabled={isDisabled}
          />
        </div>
      )}
    </div>
  );
}
